import { startAuthentication } from "@simplewebauthn/browser";
import { signInAnonymously } from "firebase/auth";
import { httpsCallableFromURL } from "firebase/functions";
import { clearChallenge } from "./clearChallenge.js";
import { FirebaseWebAuthnError } from "./FirebaseWebAuthnError.js";
import { handleVerifyFunctionResponse } from "./handleVerifyFunctionResponse.js";
/**
 * Asynchronously signs in a user with WebAuthn.
 *
 * @async
 *
 * @param auth - The {@link Auth} instance.
 * @param functions - The {@link Functions} instance.
 * @param factor - An optional {@link WebAuthnUserCredentialFactor} to be required. Not passing a value attempts both credentials if they exist.
 *
 * @returns
 *  A {@link UserCredential} when successful.
 * @throws
 *  {@link FirebaseWebAuthnError}
 */
export const signInWithPasskey = (auth, functions, factor) => ((handler) => auth.currentUser ? handler() : signInAnonymously(auth).then(() => handler(), (firebaseError) => {
    throw new FirebaseWebAuthnError({
        code: firebaseError.code.replace("firebaseWebAuthn/", ""),
        message: firebaseError.message,
        method: "signInAnonymously",
    });
}))(() => httpsCallableFromURL(functions, "/firebase-web-authn-api")({
    authenticatingCredential: factor,
    operation: "create authentication challenge",
}).then(({ data: functionResponse }) => "requestOptions" in functionResponse ? startAuthentication(functionResponse.requestOptions).then((authenticationResponse) => httpsCallableFromURL(functions, "/firebase-web-authn-api")({
    authenticationResponse: authenticationResponse,
    operation: "verify authentication",
}).then(({ data: functionResponse }) => handleVerifyFunctionResponse(auth, functionResponse)), () => clearChallenge(functions).then(() => {
    throw new FirebaseWebAuthnError({
        code: "cancelled",
        message: "Cancelled by user.",
    });
})) : "code" in functionResponse ? (() => {
    throw new FirebaseWebAuthnError(functionResponse);
})() : (() => {
    throw new FirebaseWebAuthnError({
        code: "invalid",
        message: "Invalid function response.",
    });
})(), (firebaseError) => {
    throw new FirebaseWebAuthnError({
        code: firebaseError.code.replace("firebaseWebAuthn/", ""),
        message: firebaseError.message,
        method: "httpsCallableFromURL",
        operation: "create authentication challenge",
    });
}));
